/* eslint-disable max-nested-callbacks */
import React, { useMemo, useCallback, useState } from 'react'
import i18n from 'simple-react-i18n'
import echarts from 'echarts/lib/echarts'
import ReactECharts from 'echarts-for-react'
import { countBy, groupBy, range, reverse, round, minBy, max, values, keys, template, omit, isFunction, entries, compact } from 'lodash'
import { exportExcelIcon, exportPictureIcon } from 'components/echart/EChartUtils'
import { hasValue } from 'utils/NumberUtil'
import PropTypes from 'prop-types'
import CatchmentDto from 'catchment/dto/CatchmentDto'
import moment from 'moment'
import { getContaminationState, getNitratesState, getPesticidesState } from 'catchment/utils/CatchmentUtil'
import useApplicationSetting, { intParser, listStringParser } from 'utils/customHook/useApplicationSetting'
import CatchmentPointDto from 'catchment/dto/CatchmentPointDto'
import { DEFAULT_REGROUPMENT, DEFAULT_THRESHOLD_NITRATES, DEFAULT_THRESHOLD_PESTICIDES_1, DEFAULT_THRESHOLD_PESTICIDES_2, NITRATES_CODE } from 'catchment/constants/CatchmentConstant'
import DtoAnalysisUltraLight from 'quality/dto/analyse/DtoAnalysisUltraLight'
import useStartPeriod from 'catchment/utils/useStartPeriod'
import useEndPeriod from 'catchment/utils/useEndPeriod'
import { THRESHOLD } from 'quality/constants/QualityConstants'
import { getYear } from 'utils/DateUtil'
import { getSettingBool } from 'utils/SettingUtils'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { bgGrey, blackColor, darkBlue, disabledBorderInputColor, errorColor, favOrange, INDICATORS_COLORS, LIGHTER_PASTEL_COLOR, PASTEL_COLOR, whiteColor } from 'utils/constants/ColorTheme'
import useBoolean from 'utils/customHook/useBoolean'
import { SHOW_INSEE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { THRESHOLD_COLOR, THRESHOLD_DISCRIMINANT, THRESHOLD_DISPLAY, THRESHOLD_TYPE } from 'utils/QualityThresholdUtils'
import TooltipAnalyse from 'quality/components/qualityComponents/TooltipAnalyse'
import { push } from '@lagunovsky/redux-react-router'
import { CardTable } from 'components/datatable/NewTable'
import { Chip, Dialog, DialogContent, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { exportFile } from 'utils/ExportDataUtil'
import { getI18nOrLabel } from 'utils/StringUtil'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { getLabel } from 'utils/StoreUtils'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'

const EXPORT_HEADERS = ['contamination', 'bookmark', 'havePriority', 'sensitive', 'department', 'code', 'catchment', 'mainCatchmentPoint', 'nbPoint', 'grenelle', 'buildingOwner', 'aac']

const PriorityChip = ({
    title,
    color = 'default',
}) => {
    return (
        <Chip
            label={title}
            size={'small'}
            color={color}
            sx={{ mr: 1, maxWidth: '16px', maxHeight: '16px', minHeight: '16px', minWidth: '16px', fontSize: '8px',
                '& .MuiChip-label': {
                    padding: 0,
                },
            }}

        />
    )
}

PriorityChip.propTypes = {
    title: PropTypes.string,
    color: PropTypes.string,
}

const createSeries = (key = '', color = '', opacColor = '', name = '', dataByDate = [], referenceSdages = [], formatDate = () => {}) => {
    const {
        dataLastSDAGE,
        dataOther,
    } = keys(dataByDate).reduce((acc, date) => {
        const {
            reference = 0,
            other = 0,
        } = countBy(dataByDate[date][key] || [], c => referenceSdages.includes(c.usedSdage) ? 'reference' : 'other')
        const remainingCatchments = values(dataByDate[date]).flat().length
        return {
            dataLastSDAGE: [...acc.dataLastSDAGE, { value: [formatDate(parseInt(date)), remainingCatchments === 0 ? 0 : reference / remainingCatchments * 100], name: 'reference', date }],
            dataOther: [...acc.dataOther, { value: [formatDate(parseInt(date)), remainingCatchments === 0 ? 0 : other / remainingCatchments * 100], name: 'other', date }],
        }
    }, { dataLastSDAGE: [], dataOther: [] })
    return [{
        type: 'bar',
        label: {
            show: !!dataOther.length,
            formatter: obj => {
                return round(obj.data.value[1], 2) || ''
            },
            position: 'inside',
            color: blackColor,
        },
        data: dataOther,
        name,
        stack: true,
        barMaxWidth: 60,
        itemStyle: {
            color,
        },
    }, {
        type: 'bar',
        label: {
            show: !!dataLastSDAGE.length,
            formatter: obj => {
                return round(obj.data.value[1], 2) || ''
            },
            position: 'inside',
            color: blackColor,
        },
        data: dataLastSDAGE,
        name,
        stack: true,
        barMaxWidth: 60,
        itemStyle: {
            color: opacColor,
        },
    }]
}


const createOptions = (title = '', series = [], dateList = [], exportAction = () => {}, selectedLegend = {}) => ({
    title: {
        text: title,
        left: 'center',
    },
    series,
    xAxis: [{
        type: 'category',
        data: reverse(dateList),
        axisLabel: {
            rotate: 50,
        },
    }],
    yAxis: [{
        type: 'value',
        max: 100,
        min: 0,
        name: `${i18n.catchmentPercentage} (%)`,
        nameLocation: 'middle',
        nameGap: 40,
    }],
    legend: {
        top: '5%',
        left: '10%',
        right: '20%',
        type: 'scroll',
        selected: selectedLegend,
    },
    grid: {
        top: '15%',
        left: '6%',
        right: '3%',
        bottom: '80',
        containLabel: true,
        height: 400,
    },
    tooltip: {
        trigger: 'axis',
        axisPointer: {
            type: 'shadow',
            snap: true,
        },
        formatter: (params) => {
            const sdageResult = params.filter(p => p.data.name === 'reference' && p.value[1])
            const sdageLabel = sdageResult.length ? `${i18n.referenceSDAGE}</br> ${sdageResult.map(({ marker, seriesName, value: [, value] }) => `${marker} ${seriesName}: ${round(value, 3)}%`).join('</br>')}</br>` : ''
            const otherResult = params.filter(p => p.data.name === 'other')
            const otherLabel = `${i18n.otherCatchment}</br> ${otherResult.map(({ marker, seriesName, value: [, value] }) => `${marker} ${seriesName}: ${round(value, 3)}%`).join('</br>')}`
            return `${sdageLabel}${otherLabel}`
        },
    },
    toolbox: {
        right: '3%',
        top: '5%',
        feature: {
            saveAsImage: {
                title: i18n.export,
                icon: exportPictureIcon,
                name: title,
            },
            myToolExport: isFunction(exportAction) ? {
                title: i18n.excelExport,
                icon: exportExcelIcon,
                onclick: () => exportAction(),
            } : {},
        },
    },
})

const TooltipNitrates = ({
    analyse = {},
    threshold1,
    showCode = true,
}) => (
    <TooltipAnalyse
        title={`${i18n.nitrates} ${showCode ? `[${NITRATES_CODE}]` : ''}`}
        analyse={analyse}
        threshold={{
            parameterCode: analyse.parameter,
            threshold1,
            thresholdType: THRESHOLD_TYPE.INCREASING,
            display: THRESHOLD_DISPLAY.INCREASING,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
        }}
        overrideColors={[THRESHOLD_COLOR.green, THRESHOLD_COLOR.red]}
    />
)

TooltipNitrates.propTypes = {
    analyse: PropTypes.instanceOf(DtoAnalysisUltraLight),
    threshold1: PropTypes.number,
    showCode: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
}

const ContaminationDialog = ({
    popinCards = [],
    isPopinOpen = false,
    closePopin = () => {},
    headers = [],
    exportHeaders = [],
    lineActions,
}) => {
    const dispatch = useDispatch()
    const exportRows = popinCards?.flatMap(card => values(card))?.flat()

    return (
        <Dialog
            maxWidth='lg'
            fullWidth
            open={isPopinOpen}
        >
            <DefaultDialogTitle
                title={
                    <Grid container columnSpacing={1}>
                        {i18n.catchmentsDetail}
                        <Tooltip title={
                            <Typography sx={{ maxWidth: '100%', whiteSpace: 'pre-line' }}>
                                {i18n.export}
                            </Typography>}
                        >
                            <IconButton size='large'>
                                <FileDownloadIcon
                                    sx={{ color: whiteColor }}
                                    fontSize='inherit'
                                    onClick={() => {
                                        const data = exportRows.length ? [{
                                            ...exportRows[0],
                                            headers: exportHeaders,
                                        }, ...exportRows.slice(1)] : []
                                        exportFile({
                                            data,
                                            exportType: 'xlsx',
                                            titleFile: i18n.catchmentsDetail,
                                        })
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                }
                onClose={closePopin}
            />
            <DialogContent>
                <Stack spacing={2}>
                    {popinCards.map((card, i) =>
                        (<CardTable
                            key={keys(card)?.[0] ?? i}
                            title={keys(card)?.[0]}
                            rows={values(card)?.[0]}
                            headers={headers}
                            onClickRow={s => dispatch(push(`/station/catchment/${s.id}`))}
                            color={darkBlue}
                            lineActions={lineActions}
                            hideNbElements
                        />),
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

ContaminationDialog.propTypes = {
    popinCards: PropTypes.arrayOf(PropTypes.shape({})),
    isPopinOpen: PropTypes.bool,
    closePopin: PropTypes.func,
    headers: PropTypes.arrayOf(PropTypes.oneOfType(
        [PropTypes.string, PropTypes.shape({})],
    )),
    exportHeaders: PropTypes.arrayOf(PropTypes.string),
    lineActions: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.func,
    })),
}

const lineActions = [
    { icon: (row) => row.bookmark?.value === i18n.yes ? <StarRoundedIcon sx={{ color: favOrange }} /> : undefined },
    { icon: (row) => row.havePriority?.value === i18n.yes ? <PriorityChip title={'P'} color='error'/> : undefined },
    { icon: (row) => row.sensitive?.value === i18n.yes ? <PriorityChip title={'S'} color='warning'/> : undefined },
]

const legendFilterSeries = (selectedLegend, dataByDate) => {
    const nitratesAndPesticides = selectedLegend?.[i18n.nitratesAndPesticides] === false ? 'nitratesAndPesticides' : undefined
    const nitrates = selectedLegend?.[i18n.nitrates] === false ? 'nitrates' : undefined
    const pesticides = selectedLegend?.[i18n.pesticides] === false ? 'pesticides' : undefined
    const noContamination = selectedLegend?.[i18n.noContaminationHigherThanThreshold] === false ? 'noContaminationHigherThanThreshold' : undefined
    const noData = selectedLegend?.[i18n.missingData] === false ? 'missingData' : undefined
    const omitValues = compact([nitratesAndPesticides, nitrates, pesticides, noContamination, noData])

    return keys(dataByDate).reduce((acc, act) => {
        acc[act] = omit(dataByDate[act], omitValues)
        return acc
    }, {})
}

const ContaminationNitratesEvolutionChart = ({
    catchments = [],
    catchmentPoints = [],
    analysis = [],
    useP90 = false,
    referenceSdages = [],
}) => {
    const {
        allCatchmentPoints,
        settings,
        qualitometers,
        departments,
        contributors,
        userBookmarks,
    } = useSelector(store => ({
        allCatchmentPoints: store.CatchmentReducer.catchmentPoints,
        settings: store.AdministrationReducer.applicationSettings,
        qualitometers: store.QualityReducer.qualitometersLight,
        departments: store.CityReducer.departments,
        contributors: store.ContributorReducer.contributors,
        userBookmarks: store.UserReducer.userBookmarks,
    }), shallowEqual)

    const {
        value: isPopinOpen,
        setTrue: openPopin,
        setFalse: closePopin,
    } = useBoolean(false)
    const [popinData, setPopinData] = useState({ value: [-1, ''], name: '', date: '' })
    const [selectedLegend, setSelectedLegend] = useState({})

    const thresholdNitrates = useApplicationSetting('CATCHMENT_NITRATES_THRESHOLD', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_NITRATES)
    const regroupment = useApplicationSetting('CATCHMENT_REGROUPING', setting => setting ? parseInt(setting) : DEFAULT_REGROUPMENT)
    const calculationMethod = useApplicationSetting('CATCHMENT_OVERVIEW_CALCULATION_METHOD', listStringParser)

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const optionsCalculState = useMemo(() => ({
        thresholdNitrates,
        useP90,
        calculationMethod,
    }), [thresholdNitrates, useP90, calculationMethod])

    const nitratesAnalysisGroup = useMemo(() => {
        const nitratesAnalysis = analysis.filter(({ parameter }) => parameter === NITRATES_CODE)
        return groupBy(nitratesAnalysis, STATION_TYPE_NAME.qualitometer)
    }, [analysis])

    const defaultStartPeriod = useStartPeriod(intParser)
    const minYear = useMemo(() => parseInt(getYear(minBy(analysis, 'sampleDate')?.sampleDate)) || defaultStartPeriod, [analysis, defaultStartPeriod])
    const firstCatchmentYear = getSettingBool(settings, 'FIRST_CATCHMENT_YEAR')
    const startPeriod = useMemo(() => (firstCatchmentYear ? minYear : defaultStartPeriod), [firstCatchmentYear, defaultStartPeriod, minYear])
    const endPeriod = useEndPeriod(intParser)

    const rangeDate = useMemo(() => (range(endPeriod, startPeriod - 1, -regroupment)), [endPeriod, startPeriod, regroupment])
    const formatDate = useCallback(date => (date === startPeriod || regroupment === 1 ? `${date}` : `${max([startPeriod, date - regroupment + 1])}-${date}`), [startPeriod, regroupment])
    const dateList = rangeDate.map(formatDate)

    const dataByDate = useMemo(() => (rangeDate.reduce((acc, date) => {
        acc[date] = groupBy(catchments, c => {
            const startDate = moment(date - regroupment + 1, 'YYYY').startOf('year').valueOf()
            const endDate = moment(date, 'YYYY').endOf('year').valueOf()
            const points = catchmentPoints.filter(p => p.id === c.id)
            const ids = points.map(p => qualitometers.find(q => q.code === p.codeWithoutDesignation)?.id).filter(id => hasValue(id))

            const nitrates = ids.flatMap(id => nitratesAnalysisGroup[id] || []).filter(a => a.analysisDate > startDate && a.analysisDate < endDate)
            return getNitratesState(nitrates, optionsCalculState)
        })
        return acc
    }, {})
    ), [catchments, regroupment, catchmentPoints, qualitometers, nitratesAnalysisGroup, rangeDate, optionsCalculState])

    const remainingDataByDate = useMemo(() => legendFilterSeries(selectedLegend, dataByDate), [selectedLegend, dataByDate])

    const series = useMemo(() => ([
        ...createSeries('missingData', disabledBorderInputColor, bgGrey, i18n.missingData, remainingDataByDate, referenceSdages, formatDate),
        ...createSeries('nitrates', PASTEL_COLOR.yellow, LIGHTER_PASTEL_COLOR.yellow, i18n.nitrates, remainingDataByDate, referenceSdages, formatDate),
        ...createSeries('noContaminationHigherThanThreshold', PASTEL_COLOR.blue, LIGHTER_PASTEL_COLOR.blue, i18n.noContaminationHigherThanThreshold, remainingDataByDate, referenceSdages, formatDate),
    ]), [remainingDataByDate, referenceSdages, formatDate])

    const catchmentBookmarks = useMemo(() => {
        return userBookmarks.filter(b => b.stationType === STATION_TYPE_CONSTANT.CATCHMENT).map(b => b.identifiant)
    }, [userBookmarks])

    const exportDataFormated = useMemo(() => catchments.map(catchment => {
        const points = allCatchmentPoints.filter(p => p.id === catchment.id)
        const mainPoint = points.find(p => p.mainPoint)
        const buildingOwner = mainPoint?.owner || (!!points.length && points.every(p => p.owner === points[0].owner) && points[0].owner)
        const isFav = catchmentBookmarks.some(code => code === catchment.code)
        return {
            bookmark: { value: isFav ? i18n.yes : i18n.no },
            havePriority: { value: catchment.priority ? i18n.yes : i18n.no },
            sensitive: { value: catchment.sensitive ? i18n.yes : i18n.no },
            department: { value: getLabel(departments, catchment.department, showInseeCode ? 'labelWithCode' : 'name') },
            departmentCode: catchment.department,
            code: { value: catchment.code, cellType: 'left' },
            catchment: { value: catchment.name },
            mainCatchmentPoint: { value: mainPoint?.code, cellType: 'left' },
            nbPoint: { value: points.length, cellType: 'left' },
            grenelle: { value: catchment.grenelleWork ? i18n.yes : i18n.no },
            buildingOwner: { value: buildingOwner ? getLabel(contributors, buildingOwner, 'labelDisplay', 'id') : '' },
            aac: { value: catchment.aacNum },
            id: catchment.id,
        }
    }), [catchments, allCatchmentPoints, catchmentBookmarks, departments, contributors, showInseeCode])

    const exportAction = useCallback(() => {
        const dataByPeriod = rangeDate.reverse().flatMap((date, i) => {
            const periodData = dataByDate[date]
            return catchments.map(c => {
                const contamination = periodData ? keys(periodData)?.find(key => periodData[key]?.some(catchment => catchment.id === c.id)) : ''
                return omit({
                    date: dateList[i] ?? date,
                    contamination: getI18nOrLabel(contamination),
                    ...exportDataFormated.find(catchment => catchment.id === c.id),
                }, 'id')
            })
        })
        const exportData = dataByPeriod.length ? [{ ...dataByPeriod[0], headers: ['date', ...EXPORT_HEADERS] }, ...dataByPeriod.slice(1)] : []
        exportFile({
            data: exportData,
            exportType: 'xlsx',
            titleFile: `${template(i18n.evolutionContamination)({ molecule: i18n.nitrates })}`,
        })
    }, [rangeDate, dataByDate, catchments, exportDataFormated, dateList])

    const options = useMemo(() => createOptions(
        template(i18n.evolutionContamination)({ molecule: i18n.nitrates }),
        series,
        dateList,
        exportAction,
        selectedLegend,
    ), [dateList, series, exportAction, selectedLegend])

    const cardsOrder = useMemo(() => [i18n.nitrates, i18n.noContaminationHigherThanThreshold, i18n.missingData], [])

    const popinCards = useMemo(() => {
        const yearContaminationCatchments = dataByDate[popinData.date]
        return entries(yearContaminationCatchments).map(entrie => {
            const [contamination, groupCatchment] = entrie
            const catchmentsIds = groupCatchment?.map(c => c.id)
            return catchmentsIds.map(id => ({
                contamination: getI18nOrLabel(contamination),
                ...exportDataFormated.find(c => c.id === id),
            }))
        }).map(c => groupBy(c, 'contamination')).sort((a, b) => cardsOrder.indexOf(keys(a)[0]) - cardsOrder.indexOf(keys(b)[0]))
    }, [popinData, dataByDate, exportDataFormated, cardsOrder])

    return (
        <>
            <ReactECharts
                echarts={echarts}
                option={options}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: 500 }}
                onEvents={
                    {
                        click: (params) => {
                            setPopinData(params.data)
                            openPopin()
                        },
                        legendselectchanged: legend => setSelectedLegend(legend.selected),
                    }
                }
            />
            <ContaminationDialog
                popinCards={popinCards}
                isPopinOpen={isPopinOpen}
                closePopin={closePopin}
                headers={['department', 'code', 'catchment', 'mainCatchmentPoint', 'nbPoint', 'grenelle', 'buildingOwner', 'aac']}
                exportHeaders={EXPORT_HEADERS}
                lineActions={lineActions}
            />
        </>
    )
}

ContaminationNitratesEvolutionChart.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    useP90: PropTypes.bool,
    referenceSdages: PropTypes.arrayOf(PropTypes.number),
}

const ContaminationPesticidesEvolutionChart = ({
    catchments = [],
    catchmentPoints = [],
    analysis = [],
    useP90 = false,
    referenceSdages = [],
}) => {
    const {
        allCatchmentPoints,
        settings,
        qualityThresholds,
        qualitometers,
        departments,
        contributors,
        userBookmarks,
    } = useSelector(store => ({
        allCatchmentPoints: store.CatchmentReducer.catchmentPoints,
        settings: store.AdministrationReducer.applicationSettings,
        qualityThresholds: store.QualityReducer.qualityThresholds,
        qualitometers: store.QualityReducer.qualitometersLight,
        departments: store.CityReducer.departments,
        contributors: store.ContributorReducer.contributors,
        userBookmarks: store.UserReducer.userBookmarks,
    }), shallowEqual)

    const {
        value: isPopinOpen,
        setTrue: openPopin,
        setFalse: closePopin,
    } = useBoolean(false)
    const [popinData, setPopinData] = useState({ value: [-1, ''], name: '', date: '' })
    const [selectedLegend, setSelectedLegend] = useState({})

    const thresholdPesticides1 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_1', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_1)
    const thresholdPesticides2 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_2', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_2)
    const regroupment = useApplicationSetting('CATCHMENT_REGROUPING', setting => setting ? parseInt(setting) : DEFAULT_REGROUPMENT)
    const specificThreshold = useApplicationSetting('CATCHMENT_SPECIFIC_THRESHOLD')

    const listSumPesticides = useApplicationSetting('CATCHMENT_LIST_SUM_PESTICIDES', listStringParser)
    const calculationMethod = useApplicationSetting('CATCHMENT_OVERVIEW_CALCULATION_METHOD', listStringParser)

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const threshold = useMemo(() => qualityThresholds.find(t => t.thresholdCode === specificThreshold && t.thresholdType === THRESHOLD.PC), [qualityThresholds, specificThreshold])

    const optionsCalculState = useMemo(() => ({
        thresholdPesticides1,
        thresholdPesticides2,
        useP90,
        threshold,
        listSumPesticides,
        calculationMethod,
    }), [thresholdPesticides1, thresholdPesticides2, useP90, threshold, listSumPesticides, calculationMethod])

    const pesticidesAnalysisGroup = useMemo(() => {
        const pesticidesAnalysis = analysis.filter(({ parameter }) => parameter !== NITRATES_CODE)
        return groupBy(pesticidesAnalysis, STATION_TYPE_NAME.qualitometer)
    }, [analysis])

    const defaultStartPeriod = useStartPeriod(intParser)
    const minYear = useMemo(() => parseInt(getYear(minBy(analysis, 'sampleDate')?.sampleDate)) || defaultStartPeriod, [analysis, defaultStartPeriod])
    const firstCatchmentYear = getSettingBool(settings, 'FIRST_CATCHMENT_YEAR')
    const startPeriod = useMemo(() => (firstCatchmentYear ? minYear : defaultStartPeriod), [firstCatchmentYear, defaultStartPeriod, minYear])
    const endPeriod = useEndPeriod(intParser)

    const rangeDate = useMemo(() => (range(endPeriod, startPeriod - 1, -regroupment)), [endPeriod, startPeriod, regroupment])
    const formatDate = useCallback(date => (date === startPeriod || regroupment === 1 ? `${date}` : `${max([startPeriod, date - regroupment + 1])}-${date}`), [startPeriod, regroupment])
    const dateList = rangeDate.map(formatDate)

    const dataByDate = useMemo(() => (rangeDate.reduce((acc, date) => {
        acc[date] = groupBy(catchments, c => {
            const startDate = moment(date - regroupment + 1, 'YYYY').startOf('year').valueOf()
            const endDate = moment(date, 'YYYY').endOf('year').valueOf()
            const points = catchmentPoints.filter(p => p.id === c.id)
            const ids = points.map(p => qualitometers.find(q => q.code === p.codeWithoutDesignation)?.id).filter(id => hasValue(id))

            const pesticides = ids.flatMap(id => pesticidesAnalysisGroup[id] || []).filter(a => a.analysisDate > startDate && a.analysisDate < endDate)
            return getPesticidesState(pesticides, optionsCalculState)
        })
        return acc
    }, {})
    ), [catchments, regroupment, catchmentPoints, qualitometers, pesticidesAnalysisGroup, rangeDate, optionsCalculState])

    const remainingDataByDate = useMemo(() => legendFilterSeries(selectedLegend, dataByDate), [selectedLegend, dataByDate])

    const series = useMemo(() => ([
        ...createSeries('missingData', disabledBorderInputColor, bgGrey, i18n.missingData, remainingDataByDate, referenceSdages, formatDate),
        ...createSeries('pesticides', PASTEL_COLOR.orange, LIGHTER_PASTEL_COLOR.orange, i18n.pesticides, remainingDataByDate, referenceSdages, formatDate),
        ...createSeries('noContaminationHigherThanThreshold', PASTEL_COLOR.blue, LIGHTER_PASTEL_COLOR.blue, i18n.noContaminationHigherThanThreshold, remainingDataByDate, referenceSdages, formatDate),
    ]), [remainingDataByDate, referenceSdages, formatDate])

    const catchmentBookmarks = useMemo(() => {
        return userBookmarks.filter(b => b.stationType === STATION_TYPE_CONSTANT.CATCHMENT).map(b => b.identifiant)
    }, [userBookmarks])

    const exportDataFormated = useMemo(() => catchments.map(catchment => {
        const points = allCatchmentPoints.filter(p => p.id === catchment.id)
        const mainPoint = points.find(p => p.mainPoint)
        const buildingOwner = mainPoint?.owner || (!!points.length && points.every(p => p.owner === points[0].owner) && points[0].owner)
        const isFav = catchmentBookmarks.some(code => code === catchment.code)
        return {
            bookmark: { value: isFav ? i18n.yes : i18n.no },
            havePriority: { value: catchment.priority ? i18n.yes : i18n.no },
            sensitive: { value: catchment.sensitive ? i18n.yes : i18n.no },
            department: { value: getLabel(departments, catchment.department, showInseeCode ? 'labelWithCode' : 'name') },
            departmentCode: catchment.department,
            code: { value: catchment.code, cellType: 'left' },
            catchment: { value: catchment.name },
            mainCatchmentPoint: { value: mainPoint?.code, cellType: 'left' },
            nbPoint: { value: points.length, cellType: 'left' },
            grenelle: { value: catchment.grenelleWork ? i18n.yes : i18n.no },
            buildingOwner: { value: buildingOwner ? getLabel(contributors, buildingOwner, 'labelDisplay', 'id') : '' },
            aac: { value: catchment.aacNum },
            id: catchment.id,
        }
    }), [catchments, allCatchmentPoints, catchmentBookmarks, departments, contributors, showInseeCode])

    const exportAction = useCallback(() => {
        const dataByPeriod = rangeDate.reverse().flatMap((date, i) => {
            const periodData = dataByDate[date]
            return catchments.map(c => {
                const contamination = periodData ? keys(periodData)?.find(key => periodData[key]?.some(catchment => catchment.id === c.id)) : ''
                return omit({
                    date: dateList[i] ?? date,
                    contamination: getI18nOrLabel(contamination),
                    ...exportDataFormated.find(catchment => catchment.id === c.id),
                }, 'id')
            })
        })
        const exportData = dataByPeriod.length ? [{ ...dataByPeriod[0], headers: ['date', ...EXPORT_HEADERS] }, ...dataByPeriod.slice(1)] : []
        exportFile({
            data: exportData,
            exportType: 'xlsx',
            titleFile: `${template(i18n.evolutionContamination)({ molecule: i18n.pesticides })}`,
        })
    }, [rangeDate, dataByDate, catchments, exportDataFormated, dateList])

    const options = useMemo(() => createOptions(
        template(i18n.evolutionContamination)({ molecule: i18n.pesticides }),
        series,
        dateList,
        exportAction,
        selectedLegend,
    ), [dateList, series, exportAction, selectedLegend])

    const cardsOrder = useMemo(() => [18n.pesticides, i18n.noContaminationHigherThanThreshold, i18n.missingData], [])

    const popinCards = useMemo(() => {
        const yearContaminationCatchments = dataByDate[popinData.date]
        return entries(yearContaminationCatchments).map(entrie => {
            const [contamination, groupCatchment] = entrie
            const catchmentsIds = groupCatchment?.map(c => c.id)
            return catchmentsIds.map(id => ({
                contamination: getI18nOrLabel(contamination),
                ...exportDataFormated.find(c => c.id === id),
            }))
        }).map(c => groupBy(c, 'contamination')).sort((a, b) => cardsOrder.indexOf(keys(a)[0]) - cardsOrder.indexOf(keys(b)[0]))
    }, [popinData, dataByDate, exportDataFormated, cardsOrder])

    return (
        <>
            <ReactECharts
                echarts={echarts}
                option={options}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: 500 }}
                onEvents={
                    {
                        click: (params) => {
                            setPopinData(params.data)
                            openPopin()
                        },
                        legendselectchanged: legend => setSelectedLegend(legend.selected),
                    }
                }
            />
            <ContaminationDialog
                popinCards={popinCards}
                isPopinOpen={isPopinOpen}
                closePopin={closePopin}
                headers={['department', 'code', 'catchment', 'mainCatchmentPoint', 'nbPoint', 'grenelle', 'buildingOwner', 'aac']}
                exportHeaders={EXPORT_HEADERS}
                lineActions={lineActions}
            />
        </>
    )
}

ContaminationPesticidesEvolutionChart.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    useP90: PropTypes.bool,
    referenceSdages: PropTypes.arrayOf(PropTypes.number),
}

const ContaminationEvolutionChart = ({
    catchments = [],
    catchmentPoints = [],
    analysis = [],
    useP90 = false,
    referenceSdages = [],
}) => {
    const {
        allCatchmentPoints,
        settings,
        qualityThresholds,
        qualitometers,
        departments,
        contributors,
        userBookmarks,
    } = useSelector(store => ({
        allCatchmentPoints: store.CatchmentReducer.catchmentPoints,
        settings: store.AdministrationReducer.applicationSettings,
        qualityThresholds: store.QualityReducer.qualityThresholds,
        qualitometers: store.QualityReducer.qualitometersLight,
        departments: store.CityReducer.departments,
        contributors: store.ContributorReducer.contributors,
        userBookmarks: store.UserReducer.userBookmarks,
    }), shallowEqual)

    const {
        value: isPopinOpen,
        setTrue: openPopin,
        setFalse: closePopin,
    } = useBoolean(false)
    const [popinData, setPopinData] = useState({ value: [-1, ''], name: '', date: '' })
    const [selectedLegend, setSelectedLegend] = useState({})

    const thresholdNitrates = useApplicationSetting('CATCHMENT_NITRATES_THRESHOLD', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_NITRATES)
    const thresholdPesticides1 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_1', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_1)
    const thresholdPesticides2 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_2', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_2)
    const regroupment = useApplicationSetting('CATCHMENT_REGROUPING', setting => setting ? parseInt(setting) : DEFAULT_REGROUPMENT)
    const specificThreshold = useApplicationSetting('CATCHMENT_SPECIFIC_THRESHOLD')

    const listSumPesticides = useApplicationSetting('CATCHMENT_LIST_SUM_PESTICIDES', listStringParser)
    const calculationMethod = useApplicationSetting('CATCHMENT_OVERVIEW_CALCULATION_METHOD', listStringParser)

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const threshold = useMemo(() => qualityThresholds.find(t => t.thresholdCode === specificThreshold && t.thresholdType === THRESHOLD.PC), [qualityThresholds, specificThreshold])

    const optionsCalculState = useMemo(() => ({
        thresholdNitrates,
        thresholdPesticides1,
        thresholdPesticides2,
        useP90,
        threshold,
        listSumPesticides,
        calculationMethod,
    }), [thresholdNitrates, thresholdPesticides1, thresholdPesticides2, useP90, threshold, listSumPesticides, calculationMethod])

    const nitratesAnalysisGroup = useMemo(() => {
        const nitratesAnalysis = analysis.filter(({ parameter }) => parameter === NITRATES_CODE)
        return groupBy(nitratesAnalysis, STATION_TYPE_NAME.qualitometer)
    }, [analysis])

    const pesticidesAnalysisGroup = useMemo(() => {
        const pesticidesAnalysis = analysis.filter(({ parameter }) => parameter !== NITRATES_CODE)
        return groupBy(pesticidesAnalysis, STATION_TYPE_NAME.qualitometer)
    }, [analysis])

    const defaultStartPeriod = useStartPeriod(intParser)
    const minYear = useMemo(() => parseInt(getYear(minBy(analysis, 'sampleDate')?.sampleDate)) || defaultStartPeriod, [analysis, defaultStartPeriod])
    const firstCatchmentYear = getSettingBool(settings, 'FIRST_CATCHMENT_YEAR')
    const startPeriod = useMemo(() => (firstCatchmentYear ? minYear : defaultStartPeriod), [firstCatchmentYear, defaultStartPeriod, minYear])
    const endPeriod = useEndPeriod(intParser)

    const rangeDate = useMemo(() => (range(endPeriod, startPeriod - 1, -regroupment)), [endPeriod, startPeriod, regroupment])
    const formatDate = useCallback(date => (date === startPeriod || regroupment === 1 ? `${date}` : `${max([startPeriod, date - regroupment + 1])}-${date}`), [startPeriod, regroupment])
    const dateList = rangeDate.map(formatDate)

    const dataByDate = rangeDate.reduce((acc, date) => {
        acc[date] = groupBy(catchments, c => {
            const startDate = moment(date - regroupment + 1, 'YYYY').startOf('year').valueOf()
            const endDate = moment(date, 'YYYY').endOf('year').valueOf()
            const points = catchmentPoints.filter(p => p.id === c.id)
            const ids = points.map(p => qualitometers.find(q => q.code === p.codeWithoutDesignation)?.id).filter(id => hasValue(id))

            const nitrates = ids.flatMap(id => nitratesAnalysisGroup[id] || []).filter(a => a.analysisDate > startDate && a.analysisDate < endDate)
            const pesticides = ids.flatMap(id => pesticidesAnalysisGroup[id] || []).filter(a => a.analysisDate > startDate && a.analysisDate < endDate)
            return getContaminationState(nitrates, pesticides, optionsCalculState)
        })
        return acc
    }, {})

    const remainingDataByDate = useMemo(() => legendFilterSeries(selectedLegend, dataByDate), [selectedLegend, dataByDate])

    const series = useMemo(() => ([
        ...createSeries('missingData', disabledBorderInputColor, bgGrey, i18n.missingData, remainingDataByDate, referenceSdages, formatDate),
        ...createSeries('pesticides', PASTEL_COLOR.orange, LIGHTER_PASTEL_COLOR.orange, i18n.pesticides, remainingDataByDate, referenceSdages, formatDate),
        ...createSeries('nitrates', PASTEL_COLOR.yellow, LIGHTER_PASTEL_COLOR.yellow, i18n.nitrates, remainingDataByDate, referenceSdages, formatDate),
        ...createSeries('noContaminationHigherThanThreshold', PASTEL_COLOR.blue, LIGHTER_PASTEL_COLOR.blue, i18n.noContaminationHigherThanThreshold, remainingDataByDate, referenceSdages, formatDate),
        ...createSeries('nitratesAndPesticides', errorColor, INDICATORS_COLORS.RED, i18n.nitratesAndPesticides, remainingDataByDate, referenceSdages, formatDate),
    ]), [remainingDataByDate, referenceSdages, formatDate])

    const catchmentBookmarks = useMemo(() => {
        return userBookmarks.filter(b => b.stationType === STATION_TYPE_CONSTANT.CATCHMENT).map(b => b.identifiant)
    }, [userBookmarks])

    const exportDataFormated = useMemo(() => catchments.map(catchment => {
        const points = allCatchmentPoints.filter(p => p.id === catchment.id)
        const mainPoint = points.find(p => p.mainPoint)
        const buildingOwner = mainPoint?.owner || (!!points.length && points.every(p => p.owner === points[0].owner) && points[0].owner)
        const isFav = catchmentBookmarks.some(code => code === catchment.code)
        return {
            bookmark: { value: isFav ? i18n.yes : i18n.no },
            havePriority: { value: catchment.priority ? i18n.yes : i18n.no },
            sensitive: { value: catchment.sensitive ? i18n.yes : i18n.no },
            department: { value: getLabel(departments, catchment.department, showInseeCode ? 'labelWithCode' : 'name') },
            departmentCode: catchment.department,
            code: { value: catchment.code, cellType: 'left' },
            catchment: { value: catchment.name },
            mainCatchmentPoint: { value: mainPoint?.code, cellType: 'left' },
            nbPoint: { value: points.length, cellType: 'left' },
            grenelle: { value: catchment.grenelleWork ? i18n.yes : i18n.no },
            buildingOwner: { value: buildingOwner ? getLabel(contributors, buildingOwner, 'labelDisplay', 'id') : '' },
            aac: { value: catchment.aacNum },
            id: catchment.id,
        }
    }), [catchments, allCatchmentPoints, catchmentBookmarks, departments, contributors, showInseeCode])

    const exportAction = useCallback(() => {
        const dataByPeriod = rangeDate.reverse().flatMap((date, i) => {
            const periodData = dataByDate[date]
            return catchments.map(c => {
                const contamination = periodData ? keys(periodData)?.find(key => periodData[key]?.some(catchment => catchment.id === c.id)) : ''
                return omit({
                    date: dateList[i] ?? date,
                    contamination: getI18nOrLabel(contamination),
                    ...exportDataFormated.find(catchment => catchment.id === c.id),
                }, 'id')
            })
        })
        const exportData = dataByPeriod.length ? [{ ...dataByPeriod[0], headers: ['date', ...EXPORT_HEADERS] }, ...dataByPeriod.slice(1)] : []
        exportFile({
            data: exportData,
            exportType: 'xlsx',
            titleFile: `${i18n.evolutionContaminationNitratesPesticides}`,
        })
    }, [rangeDate, dataByDate, catchments, exportDataFormated, dateList])

    const options = useMemo(() => createOptions(
        i18n.evolutionContaminationNitratesPesticides,
        series,
        dateList,
        exportAction,
        selectedLegend,
    ), [dateList, series, exportAction, selectedLegend])

    const cardsOrder = useMemo(() => [i18n.nitratesAndPesticides, i18n.nitrates, i18n.pesticides, i18n.noContaminationHigherThanThreshold, i18n.missingData], [])

    const popinCards = useMemo(() => {
        const yearContaminationCatchments = dataByDate[popinData.date]
        return entries(yearContaminationCatchments).map(entrie => {
            const [contamination, groupCatchment] = entrie
            const catchmentsIds = groupCatchment?.map(c => c.id)
            return catchmentsIds.map(id => ({
                contamination: getI18nOrLabel(contamination),
                ...exportDataFormated.find(c => c.id === id),
            }))
        }).map(c => groupBy(c, 'contamination')).sort((a, b) => cardsOrder.indexOf(keys(a)[0]) - cardsOrder.indexOf(keys(b)[0]))
    }, [popinData, dataByDate, exportDataFormated, cardsOrder])

    return (
        <>
            <ReactECharts
                echarts={echarts}
                option={options}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: 500 }}
                onEvents={
                    {
                        click: (params) => {
                            setPopinData(params.data)
                            openPopin()
                        },
                        legendselectchanged: legend => setSelectedLegend(legend.selected),
                    }
                }
            />
            <ContaminationDialog
                popinCards={popinCards}
                isPopinOpen={isPopinOpen}
                closePopin={closePopin}
                headers={['department', 'code', 'catchment', 'mainCatchmentPoint', 'nbPoint', 'grenelle', 'buildingOwner', 'aac']}
                exportHeaders={EXPORT_HEADERS}
                lineActions={lineActions}
            />
        </>
    )
}

ContaminationEvolutionChart.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    useP90: PropTypes.bool,
    referenceSdages: PropTypes.arrayOf(PropTypes.number),
}

export { ContaminationNitratesEvolutionChart, ContaminationPesticidesEvolutionChart, ContaminationEvolutionChart }